import React from 'react';

import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockCheckbox from '../components/GuideMockCheckbox';
import GuideMockContainer from '../components/GuideMockContainer';
import GuideMockRadioGroup from '../components/GuideMockRadioGroup';
import GuideMockSelect from '../components/GuideMockSelect';
import GuideMockTextField from '../components/GuideMockTextField';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideContractNewRequest: React.FC = () => {
  return (
    <GuidePageLayout
      title="新規契約を申請する"
      description="新しい要員の契約を申請し、承認プロセスを開始します。"
      breadcrumbs={[{ label: '契約管理', path: '/guide/contract/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者 / 管理部門 / 課長'],
            ['ログイン状態', 'ログイン済みであること'],
            ['事前準備', 'プロジェクト・要員が登録済みであること'],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: '契約管理メニューから新規申請画面を開く' },
            {
              label: '基本情報セクションを入力する',
              content: (
                <GuideMockContainer label="基本情報">
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <GuideMockSelect label="担当者" options={['山田太郎']} required />
                    </Grid>
                    <Grid size={6}>
                      <GuideMockSelect label="課長" options={['鈴木一郎']} required />
                    </Grid>
                    <Grid size={6}>
                      <GuideMockSelect label="プロジェクト" options={['プロジェクトA']} required />
                    </Grid>
                    <Grid size={6}>
                      <GuideMockTextField label="取引先" disabled value="自動表示" />
                    </Grid>
                  </Grid>
                </GuideMockContainer>
              ),
            },
            {
              label: 'メンバー情報セクションを入力する',
              content: (
                <GuideMockContainer label="メンバー情報">
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <GuideMockSelect label="メンバー" options={['佐藤太郎']} required />
                    </Grid>
                    <Grid size={6}>
                      <GuideMockTextField label="パートナー" disabled value="自動表示" />
                    </Grid>
                    <Grid size={6}>
                      <GuideMockSelect label="都道府県" options={['東京都', '大阪府']} />
                    </Grid>
                    <Grid size={6}>
                      <GuideMockTextField label="ロケーション" />
                    </Grid>
                  </Grid>
                </GuideMockContainer>
              ),
            },
            {
              label: '契約情報セクションを入力する',
              content: (
                <GuideMockContainer label="契約情報">
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <GuideMockRadioGroup label="契約形態" options={['派遣', 'SES', '業務委託']} />
                    </Grid>
                    <Grid size={6}>
                      <GuideMockRadioGroup label="契約単位" options={['月額', '時間']} />
                    </Grid>
                    <Grid size={6}>
                      <GuideMockTextField label="契約開始日" />
                    </Grid>
                    <Grid size={6}>
                      <GuideMockTextField label="契約終了日" />
                    </Grid>
                    <Grid size={6}>
                      <GuideMockTextField label="契約金額（原価）" />
                    </Grid>
                    <Grid size={6}>
                      <GuideMockTextField label="契約金額（売上）" />
                    </Grid>
                    <Grid size={6}>
                      <GuideMockTextField label="稼働率" value="100" />
                    </Grid>
                    <Grid size={3}>
                      <GuideMockTextField label="精算幅（上限）" />
                    </Grid>
                    <Grid size={3}>
                      <GuideMockTextField label="精算幅（下限）" />
                    </Grid>
                  </Grid>
                </GuideMockContainer>
              ),
            },
            {
              label: '補足情報・特記事項を入力する（任意）',
              content: (
                <GuideMockContainer label="補足情報">
                  <Stack spacing={1}>
                    <GuideMockCheckbox label="原籍確認シート: 必要" />
                    <GuideMockTextField label="備考" />
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '一時保存または申請を選択する',
              description: '一時保存は後で修正可能。申請すると申請先に通知が送信されます。',
              content: (
                <GuideMockContainer label="操作ボタン">
                  <Stack direction="row" spacing={1.5}>
                    <GuideMockButton label="一時保存" variant="outlined" startIcon={<SaveIcon />} />
                    <GuideMockButton label="申請" startIcon={<SendIcon />} />
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '申請の場合は申請者・申請先を選択する',
              description: '確認ダイアログで申請者リストと申請先リストを入力します。',
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="主要入力項目">
        <GuideTable
          headers={['項目名', '一時保存', '申請時', '備考']}
          rows={[
            ['担当者', '必須', '必須', 'ユーザーから選択'],
            ['プロジェクト', '必須', '必須', '登録済みプロジェクトから選択'],
            ['課長', '必須', '必須', '課長権限ユーザーから選択'],
            ['メンバー', '必須', '必須', '登録済み要員から選択'],
            ['契約開始日', '任意', '必須', '月初日'],
            ['契約終了日', '任意', '必須', '月末日'],
            ['契約金額（原価）', '任意', '必須', '数値'],
            ['契約金額（売上）', '任意', '必須', '数値'],
            ['申請者', '—', '必須', '1名以上'],
            ['申請先', '—', '必須', '1名以上'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox>一時保存（ADJUSTING）の場合は後で内容を修正して申請できます。</GuideInfoBox>
          <GuideInfoBox>管理ID（REQ-連番）が自動発行されます。</GuideInfoBox>
          <GuideInfoBox>
            テンプレート項目はテンプレート管理で設定された動的項目がフォーム下部に表示されます。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideContractNewRequest;
